import { Inventory, War } from '../types';

export const PIECE_VALUES: Record<keyof Inventory, number> = {
    p: 1,
    n: 3,
    b: 3,
    r: 5,
    q: 9,
    k: 0 // King valueless for resource calculation? Or infinite?
};

// Royal Commission Costs (Pawn Equivalents)
export const COMMISSION_COSTS: Record<string, number> = {
    n: 6,
    b: 6,
    r: 8,
    q: 12
};

export const SOVEREIGNTY_ZONES = {
    ABDICATION: { min: 0, max: 0.5, label: 'ABDICATION' },
    CONFLICT: { min: 0.5, max: 2.0, label: 'CONFLICT' },
    DOMINION: { min: 2.0, max: Infinity, label: 'DOMINION' }
};

export const calculateCasualties = (initial: Inventory, final: Inventory): Inventory => {
    const casualties: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
    (Object.keys(initial) as Array<keyof Inventory>).forEach(key => {
        // If we have fewer pieces now than before, the difference is casualties.
        // NOTE: This assumes pieces were NOT added during battle (which they aren't in standard chess, except promotion).
        // Promotion complicates this: A Pawn becomes a Queen.
        // Initial: 8P, 1Q. Final: 7P, 2Q.
        // Did we lose a Pawn? Yes, it promoted.
        // "Losses are permanent". Does a promoted pawn count as a "loss" of a pawn?
        // Usually, in persistent chess, a promoted piece STAYS promoted.
        // So if I started with 8P, and ended with 7P and 2Q (one promoted), I have technically "lost" a pawn and "gained" a queen.
        // But for "The Butcher's Bill", we probably want to track *deaths* (captures).
        // Our Battle.tsx already tracks `casualties` via captures.
        // So we might not need to calculate it from diffs if we trust the battle report.
        // However, for the persistent inventory update, we need to apply the net change.

        // Let's rely on the explicit 'casualties' object passed from the Battle for the "Bill".
        // But for the WAR STATE update, we need to reconcile promotions.
        // If Battle.tsx only reports captured pieces as casualties, we miss the promotion "gain".
        // We should probably trust the `Inventory` passed out of the battle if we were tracking it fully.
        // But Battle.tsx currently calculates casualties by looking at *captures*.
        // It does NOT track promotions adding to inventory.
        // Let's stick to the prompt's simplicity for now: "Casualties = Initial - Final".
        // If a pawn promoted, it is no longer a pawn.
        // But we want to keep the Queen!
        // So: NewInventory = OldInventory - Casualties + Promotions?
        // The current `MockSupabase` logic simply subtracts casualties.
        // This means promotions are currently LOST (revert to pawns? no, just lost).
        // MVP: Let's stick to Battle.tsx capture-tracking for the visual report.

        // For this helper, let's just do simple diff logic if needed, 
        // but primarily we might just need helpers for ratio.
        casualties[key] = Math.max(0, initial[key] - final[key]); // This is naive diff.
    });
    return casualties;
};

export const getSovereigntyStatus = (playerPawns: number, enemyPawns: number): { label: string, ratio: number } => {
    // Avoid division by zero
    const safeEnemy = Math.max(1, enemyPawns);
    const ratio = playerPawns / safeEnemy;

    if (ratio <= SOVEREIGNTY_ZONES.ABDICATION.max) return { label: SOVEREIGNTY_ZONES.ABDICATION.label, ratio };
    if (ratio >= SOVEREIGNTY_ZONES.DOMINION.min) return { label: SOVEREIGNTY_ZONES.DOMINION.label, ratio };
    return { label: SOVEREIGNTY_ZONES.CONFLICT.label, ratio };
};

export const canAffordCommission = (inventory: Inventory, unitType: string): boolean => {
    const cost = COMMISSION_COSTS[unitType];
    if (!cost) return false;
    return inventory.p >= cost; // Simple check, doesn't account for "leaving 1 pawn" or anything unless specified
};
